import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useUpdateWorkflow, type PlainWorkflow } from "@/queries/automation-queries";
import { workflowName } from "@/utils/automations";
import { formatError } from "@/utils/errors";
import { useCallback, useId, useState, type FC, type FormEvent } from "react";

type RenameAutomationDialogProps = {
    workflow: PlainWorkflow;
    open: boolean;
    onClose: () => void;
    onRenamed?: (name: string, description: string) => void;
    /** When true, skip API call and only update local state via onRenamed */
    isTemplate?: boolean;
};

export const RenameAutomationDialog: FC<RenameAutomationDialogProps> = ({
    open,
    workflow,
    onClose,
    onRenamed,
    isTemplate = false,
}) => {
    const { toast } = useToast();
    const updateWorkflow = useUpdateWorkflow();

    const [name, setName] = useState<string | undefined>();
    const [description, setDescription] = useState<string | undefined>();

    const currentName = name ?? workflowName(workflow) ?? "";
    const currentDescription = description ?? workflow.metadata?.description ?? "";

    const inputNameId = useId();
    const inputDescriptionId = useId();

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                setName(undefined);
                setDescription(undefined);
                onClose();
            }
        },
        [onClose],
    );

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (!currentName.trim()) {
                return;
            }

            // For templates, just update local state without API call
            if (isTemplate) {
                onRenamed?.(currentName.trim(), currentDescription.trim());
                setName(undefined);
                setDescription(undefined);
                onClose();
                return;
            }

            try {
                await updateWorkflow.mutateAsync({
                    workflowId: workflow.id,
                    name: currentName.trim(),
                    description: currentDescription.trim(),
                });
                onRenamed?.(currentName.trim(), currentDescription.trim());
                setName(undefined);
                setDescription(undefined);
                onClose();
            } catch (error) {
                toast({
                    title: "Failed to rename automation",
                    description: formatError(error),
                });
            }
        },
        [currentName, currentDescription, updateWorkflow, workflow.id, onClose, toast, onRenamed, isTemplate],
    );

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-[400px]" data-track-location={TrackLocations.RenameAutomationDialog}>
                <DialogHeader>
                    <DialogTitle>Rename automation</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <DialogBody>
                        <div className="flex flex-col gap-4">
                            <div className="space-y-1">
                                <Label htmlFor={inputNameId}>Name</Label>
                                <Input
                                    id={inputNameId}
                                    type="text"
                                    name="automation_name"
                                    value={currentName}
                                    onChange={(e) => setName(e.target.value)}
                                    autoFocus
                                    data-1p-ignore
                                    data-lpignore="true"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor={inputDescriptionId}>Description</Label>
                                <Textarea
                                    id={inputDescriptionId}
                                    name="automation_description"
                                    value={currentDescription}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={3}
                                    dontAutosize
                                    className="resize-none"
                                />
                            </div>
                        </div>
                    </DialogBody>

                    <DialogFooter className="justify-end">
                        <DialogClose asChild>
                            <Button type="button" variant="outline">
                                Cancel
                            </Button>
                        </DialogClose>
                        <Button
                            loading={updateWorkflow.isPending}
                            type="submit"
                            disabled={!currentName.trim()}
                            variant="brand"
                            data-tracking-id="confirm-rename-automation-rename-automation-dialog"
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
